/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { cssMap, jsx, cx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import { RovoThinkingIndicator } from '../thinking-indicator/ThinkingIndicator';

import messages from './messages';
import { type RovoThinkingBarStatus } from './RovoThinkingBar';

const styles = cssMap({
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		width: 'fit-content',
		maxWidth: '100%',
		boxSizing: 'border-box',
		paddingLeft: token('space.075'),
		paddingRight: token('space.100'),
		paddingBlock: token('space.075'),
		borderRadius: token('radius.full'),
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	pressable: {
		border: 'none',
		cursor: 'pointer',
		textAlign: 'start',
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('elevation.surface.overlay.hovered'),
		},
		'&:active': {
			transition: token('motion.button.pressed'),
			backgroundColor: token('elevation.surface.overlay.pressed'),
		},
	},
});

export type RovoThinkingPillProps = {
	/**
	 * Controls which short loading message is shown. Defaults to `'analyzing'`.
	 */
	status?: RovoThinkingBarStatus;
	/**
	 * Overrides the default short loading text. Must already be localized by the
	 * caller; it is rendered as-is without going through `react-intl`.
	 */
	label?: string;
	/**
	 * When provided, the pill becomes a clickable button (e.g. wired to a
	 * `toggleMinimized`/restore handler). Its accessible name is derived from
	 * the pill's own visible text (`label`, or the default short loading text).
	 */
	onClick?: () => void;
	testId?: string;
};

/**
 * A compact, pill-shaped "minimized" variant of the Rovo thinking bar.
 *
 * Renders only the shape-morphing loading animation (via `RovoThinkingIndicator`)
 * alongside a short loading text — with no action buttons, progress bar, or the
 * animated mosaic gradient border used by `RovoThinkingBar`/`RovoThinkingBarWrapper`.
 *
 * Use this where space is constrained and a full thinking bar would be too large.
 *
 * Usage:
 * ```tsx
 * <RovoThinkingPill status="analyzing" />
 * ```
 */
export const RovoThinkingPill = ({
	status = 'analyzing',
	label,
	onClick,
	testId = 'rovo-thinking-pill',
}: RovoThinkingPillProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const title =
		label ??
		formatMessage(status === 'editing' ? messages.editingTextShort : messages.thinkingTextShort);

	const indicator = <RovoThinkingIndicator title={title} animation />;

	if (onClick) {
		return (
			<Pressable xcss={cx(styles.pill, styles.pressable)} onClick={onClick} testId={testId}>
				{indicator}
			</Pressable>
		);
	}

	return (
		<Box xcss={styles.pill} testId={testId}>
			{indicator}
		</Box>
	);
};
